// AreaFormPage の配合比計算ロジック。
// 使用材料一覧(工程ベース)の行を配合比マスタから導出する純関数群。

import { useFullDb } from "../../../api/dbSlice";

export type FullDb = ReturnType<typeof useFullDb>["db"];

// 使用材料一覧(工程ベース)の1行。主材料行のみ計画数量を入力でき、従属材料行は配合比から自動計算する。
export interface ProcMatRow {
  rowKey: string;
  workDetailId: number;
  workDetailName: string;
  materialName: string;
  detailName: string;
  detailUnit: string;
  isMain: boolean;
  ratioText: string;
  qty: number | null;
  qtyMin: number | null;
  qtyMax: number | null;
  referenceStandardUsage: number | null;
  referenceStandardUsageUnit: string | null;
  materialRemarks: string | null;
}

export type WorkDetailRow = { id: number; work_subtype_id: number; mix_ratio_definition_id: number | null; name: string; deleted_at: number | null };

// 1工程を「主材料 + 従属材料」の行へフラット展開する。
// 主材料の計画数量は mainQty、従属材料は配合比比率(subMid / mainRatio)で自動計算する。
export function procRowsOfWorkDetail(db: FullDb, wd: WorkDetailRow, mainQty: number | null): ProcMatRow[] {
  if (wd.mix_ratio_definition_id == null) {
    return [];
  }
  const wdMixId = wd.mix_ratio_definition_id;
  const mixDef = db.mixRatioDefinitions.find((m) => m.deleted_at == null && m.id === wdMixId);
  if (mixDef === undefined) {
    return [];
  }
  const mixDefId = mixDef.id;
  const details = db.mixRatioDetails
    .filter((d) => d.deleted_at == null && d.mix_ratio_definition_id === mixDefId)
    .sort((a, b) => a.id - b.id);
  if (details.length === 0) {
    return [];
  }
  const material = db.materials.find((m) => m.id === mixDef.material_id);
  const mainRatio = details[0]?.ratio_start ?? 0;
  return details.map((d, i) => {
    const md = db.materialDetails.find((x) => x.id === d.material_detail_id);
    const isMain = i === 0;
    const subMid = d.ratio_end != null ? (d.ratio_start + d.ratio_end) / 2 : d.ratio_start;
    const qty = isMain
      ? mainQty
      : (mainQty == null || mainRatio === 0 ? null : mainQty * (subMid / mainRatio));
    const qtyMin = isMain || mainQty == null || mainRatio === 0
      ? null
      : mainQty * (d.ratio_start / mainRatio);
    const qtyMax = isMain || mainQty == null || mainRatio === 0 || d.ratio_end == null
      ? null
      : mainQty * (d.ratio_end / mainRatio);
    const ratioText = d.ratio_end != null ? `${d.ratio_start}〜${d.ratio_end}` : `${d.ratio_start}`;
    return {
      rowKey: `${wd.id}-${d.id}`,
      workDetailId: wd.id,
      workDetailName: wd.name,
      materialName: material?.name ?? "-",
      detailName: md?.detail_name ?? "-",
      detailUnit: md?.content_unit ?? "",
      isMain,
      ratioText,
      qty,
      qtyMin,
      qtyMax,
      referenceStandardUsage: isMain ? material?.standard_usage ?? null : null,
      referenceStandardUsageUnit: isMain ? material?.standard_usage_unit ?? null : null,
      materialRemarks: isMain ? material?.remarks ?? null : null,
    };
  });
}

// 種別配下の工程を「主材料 + 従属材料」の行へフラット展開する(mock UsedMaterialsTable と同ロジック)。
// 主材料の計画数量は plannedMainQtyMap(work_detail_id → kg)から取る。
export function deriveProcMatRows(db: FullDb, workSubtypeId: number | null, plannedMainQtyMap: Record<number, number>): ProcMatRow[] {
  if (workSubtypeId == null) {
    return [];
  }
  const workDetails = db.workDetails
    .filter((d) => d.deleted_at == null && d.work_subtype_id === workSubtypeId)
    .sort((a, b) => a.id - b.id);
  let rows: ProcMatRow[] = [];
  for (const wd of workDetails) {
    const mainQty = plannedMainQtyMap[wd.id] ?? null;
    rows = rows.concat(procRowsOfWorkDetail(db, wd, mainQty));
  }
  return rows;
}

// 工程 → 主材料細目の単位(content_unit)。保存時に主材料行の unit として使う。
export function mainDetailUnitOfWorkDetail(db: FullDb, workDetailId: number): string {
  const wd = db.workDetails.find((d) => d.id === workDetailId);
  if (wd === undefined || wd.mix_ratio_definition_id == null) {
    return "";
  }
  // var はコールバック内で narrowing が効かないため、ガード後の確定値をローカルへ退避してから使う。
  const mixDefId = wd.mix_ratio_definition_id;
  const mixDef = db.mixRatioDefinitions.find((m) => m.deleted_at == null && m.id === mixDefId);
  if (mixDef === undefined) {
    return "";
  }
  const mixDefResolvedId = mixDef.id;
  const mainDetail = db.mixRatioDetails
    .filter((d) => d.deleted_at == null && d.mix_ratio_definition_id === mixDefResolvedId)
    .sort((a, b) => a.id - b.id)[0];
  if (mainDetail === undefined) {
    return "";
  }
  return db.materialDetails.find((x) => x.id === mainDetail.material_detail_id)?.content_unit ?? "";
}

// 工程 → 主材料 material_id。保存時に工程由来行の material_id として使う。
export function mainMaterialIdOfWorkDetail(db: FullDb, workDetailId: number): number | null {
  const wd = db.workDetails.find((d) => d.id === workDetailId);
  if (wd === undefined || wd.mix_ratio_definition_id == null) {
    return null;
  }
  const mixDefId = wd.mix_ratio_definition_id;
  const mixDef = db.mixRatioDefinitions.find((m) => m.deleted_at == null && m.id === mixDefId);
  return mixDef?.material_id ?? null;
}
